// Browser helper. No Sherpa client credential or customer session token belongs here.
function secureUrl(value, base) {
  const url = new URL(value, base);
  const local = url.hostname === "localhost" || url.hostname.endsWith(".localhost") || url.hostname === "127.0.0.1";
  if ((url.protocol !== "https:" && !(url.protocol === "http:" && local)) || url.username || url.password) {
    throw new Error("An HTTPS URL is required");
  }
  return url;
}

function isReady(data) {
  return data && typeof data === "object" && data.type === "sherpa:customer-portal:ready" &&
    typeof data.requestId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.requestId) &&
    typeof data.codeChallenge === "string" && /^[A-Za-z0-9_-]{43}$/.test(data.codeChallenge);
}

function iframeHeight(iframe, host, contentHeight) {
  const style = typeof host.getComputedStyle === "function" ? host.getComputedStyle(iframe) : null;
  if (style?.boxSizing === "border-box") {
    const inset = [
      style.borderTopWidth,
      style.borderBottomWidth,
      style.paddingTop,
      style.paddingBottom,
    ].reduce((total, value) => {
      const pixels = Number.parseFloat(value);
      return total + (Number.isFinite(pixels) ? pixels : 0);
    }, 0);
    return Math.ceil(contentHeight + inset);
  }
  if (!style && Number.isFinite(iframe.offsetHeight) && Number.isFinite(iframe.clientHeight)) {
    return Math.ceil(contentHeight + Math.max(0, iframe.offsetHeight - iframe.clientHeight));
  }
  return Math.ceil(contentHeight);
}

function validateBootstrap(value, portal, parentOrigin) {
  if (!value || typeof value !== "object" || typeof value.bootstrapCode !== "string" ||
      !/^[A-Za-z0-9_-]{43}$/.test(value.bootstrapCode) || typeof value.expiresAt !== "string" ||
      !(Date.parse(value.expiresAt) > Date.now()) || typeof value.iframeUrl !== "string") {
    throw new Error("Invalid customer portal bootstrap");
  }
  const target = secureUrl(value.iframeUrl);
  const publicKeys = new Set(["parentOrigin", "locale"]);
  if (target.origin !== portal.origin || target.pathname !== portal.pathname || target.hash ||
      target.searchParams.get("parentOrigin") !== parentOrigin ||
      Array.from(target.searchParams.keys()).some((key) => !publicKeys.has(key))) {
    throw new Error("Unexpected customer portal destination");
  }
  return value.bootstrapCode;
}

/** Call once per iframe. Call the returned cleanup function when removing it. */
export function mountCustomerPortal(iframe, { portalUrl, bootstrapUrl, requestBootstrap, onError, fetch: authenticatedFetch }) {
  const host = iframe.ownerDocument.defaultView;
  if (!host || !iframe.contentWindow || typeof onError !== "function") {
    throw new Error("An attached iframe and an error handler are required");
  }
  const parent = secureUrl(host.location.origin);
  const portal = secureUrl(portalUrl);
  if ((typeof bootstrapUrl === "string") === (typeof requestBootstrap === "function")) {
    throw new Error("Provide either a backend endpoint or a bootstrap callback");
  }
  const endpoint = typeof bootstrapUrl === "string" ? secureUrl(bootstrapUrl, parent.origin) : null;
  if (authenticatedFetch !== undefined && typeof authenticatedFetch !== "function") {
    throw new Error("The authenticated fetch must be a function");
  }
  const publicKeys = new Set(["parentOrigin", "locale"]);
  if ((endpoint && (endpoint.origin !== parent.origin || endpoint.search || endpoint.hash)) || portal.hash ||
      !portal.pathname.startsWith("/embed-customer-portal/") ||
      Array.from(portal.searchParams.keys()).some((key) => !publicKeys.has(key))) {
    throw new Error("Use a clean iframe URL and a backend endpoint on your own website");
  }
  portal.searchParams.set("parentOrigin", parent.origin);
  let active = null;
  let disposed = false;

  async function handleReady(ready) {
    if (active?.requestId === ready.requestId) return;
    active?.controller.abort();
    const current = { requestId: ready.requestId, controller: new AbortController() };
    active = current;
    const timer = setTimeout(() => current.controller.abort(), 15_000);
    try {
      let bootstrap;
      if (requestBootstrap) {
        bootstrap = await requestBootstrap({
          requestId: ready.requestId,
          codeChallenge: ready.codeChallenge,
        }, current.controller.signal);
      } else {
        const response = await (authenticatedFetch || host.fetch.bind(host))(endpoint.toString(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          cache: "no-store",
          redirect: "error",
          signal: current.controller.signal,
          body: JSON.stringify({ requestId: ready.requestId, codeChallenge: ready.codeChallenge }),
        });
        if (!response.ok) throw new Error("Customer portal bootstrap request failed");
        bootstrap = await response.json();
      }
      const bootstrapCode = validateBootstrap(bootstrap, portal, parent.origin);
      if (disposed || active !== current) return;
      iframe.contentWindow?.postMessage({
        type: "sherpa:customer-portal:bootstrap",
        requestId: ready.requestId,
        bootstrapCode,
      }, portal.origin);
    } catch {
      if (disposed || active !== current) return;
      iframe.contentWindow?.postMessage({ type: "sherpa:customer-portal:error", requestId: ready.requestId }, portal.origin);
      onError(new Error("Could not open the customer portal. Please try again."));
    } finally {
      clearTimeout(timer);
    }
  }

  function onMessage(event) {
    if (disposed || event.source !== iframe.contentWindow || event.origin !== portal.origin) return;
    if (isReady(event.data)) {
      void handleReady(event.data);
      return;
    }
    if (event.data?.type === "sherpa:resize" && Number.isFinite(event.data.height) && event.data.height > 0 && event.data.height <= 100_000) {
      iframe.style.height = `${iframeHeight(iframe, host, event.data.height)}px`;
    }
  }

  host.addEventListener("message", onMessage);
  iframe.referrerPolicy = "no-referrer";
  iframe.src = portal.toString();
  return function dispose() {
    disposed = true;
    active?.controller.abort();
    host.removeEventListener("message", onMessage);
  };
}

/** Create and manage an embedded customer portal within a partner page. */
function mount(containerOrSelector, options) {
  const container = typeof containerOrSelector === "string"
    ? document.querySelector(containerOrSelector)
    : containerOrSelector;
  if (!container || typeof container.appendChild !== "function" || !container.ownerDocument?.defaultView) {
    throw new Error("A customer portal container is required");
  }
  if (!options || typeof options.portalUrl !== "string") {
    throw new Error("A clean customer portal URL is required");
  }
  const doc = container.ownerDocument;
  const host = doc.defaultView;
  const portal = secureUrl(options.portalUrl);
  if (options.locale !== undefined) {
    if (typeof options.locale !== "string" || !options.locale.trim()) {
      throw new Error("A valid portal locale is required");
    }
    portal.searchParams.set("locale", options.locale);
  }
  const message = doc.createElement("div");
  message.hidden = true;
  message.setAttribute("role", "alert");
  const label = doc.createElement("p");
  label.textContent = "Could not open your insurance. Please try again.";
  const retry = doc.createElement("button");
  retry.type = "button";
  retry.textContent = "Try again";
  message.appendChild(label);
  message.appendChild(retry);
  let iframe = null;
  let dispose = null;
  let destroyed = false;
  let suspended = false;

  function clearFrame() {
    dispose?.();
    dispose = null;
    iframe?.remove();
    iframe = null;
  }

  function reload() {
    if (destroyed) return;
    clearFrame();
    message.hidden = true;
    const next = doc.createElement("iframe");
    next.title = "Your insurance";
    next.style.display = "block";
    next.style.width = "100%";
    next.style.border = "0px";
    container.appendChild(next);
    iframe = next;
    try {
      dispose = mountCustomerPortal(next, {
        portalUrl: portal.toString(),
        bootstrapUrl: options.bootstrapUrl ?? "/api/portal-session",
        fetch: options.fetch,
        onError() {
          if (destroyed || iframe !== next) return;
          clearFrame();
          message.hidden = false;
        },
      });
    } catch (error) {
      clearFrame();
      throw error;
    }
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    host.removeEventListener("pagehide", onPageHide);
    host.removeEventListener("pageshow", onPageShow);
    retry.removeEventListener("click", reload);
    clearFrame();
    message.remove();
  }

  function onPageHide() {
    if (destroyed) return;
    suspended = true;
    clearFrame();
    message.hidden = true;
  }

  function onPageShow(event) {
    if (destroyed || !suspended || !event.persisted) return;
    suspended = false;
    reload();
  }

  container.appendChild(message);
  retry.addEventListener("click", reload);
  host.addEventListener("pagehide", onPageHide);
  host.addEventListener("pageshow", onPageShow);
  try {
    reload();
  } catch (error) {
    destroy();
    throw error;
  }
  return { destroy, reload };
}

export default { mount };
